/**
 * ParentGoalsView — read-only rendering of every goal on file for
 * the parent's kid(s). The "IEP link" surface in the parent
 * dashboard.
 *
 * Renders from the `goal_texts` field on the api-get-practice-rollup
 * response, NOT by hitting the `goals` table directly. This keeps
 * the parent on the service-role-mediated read path that the rollup
 * function exposes, and avoids the parent hitting the goals RLS.
 *
 * No edit affordances. Parents who want to add or change a goal go
 * through their SLP.
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../../lib/supabase/client';
import type { GoalText, RollupResponse } from './parentTypes';

interface Props {
  practiceSetId: string | null;
  onBack: () => void;
}

export function ParentGoalsView({
  practiceSetId,
  onBack,
}: Props): ReactElement {
  const [goals, setGoals] = useState<GoalText[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!practiceSetId) {
      setGoals([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      const { data, error: rollupErr } = await supabase.functions.invoke<
        RollupResponse
      >('api-get-practice-rollup', {
        body: { practice_set_id: practiceSetId },
      });
      if (cancelled) return;
      if (rollupErr) {
        setError(rollupErr.message);
        setLoading(false);
        return;
      }
      setGoals(data?.goal_texts ?? []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [practiceSetId]);

  if (!practiceSetId) {
    return (
      <main className="screen parent-goals">
        <button type="button" className="link-button" onClick={onBack}>
          ← Back to dashboard
        </button>
        <p className="tagline">No practice set to show goals for.</p>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="screen parent-goals">
        <button type="button" className="link-button" onClick={onBack}>
          ← Back to dashboard
        </button>
        <p className="tagline">Loading goals…</p>
      </main>
    );
  }

  if (error) {
    return (
      <main className="screen parent-goals">
        <button type="button" className="link-button" onClick={onBack}>
          ← Back to dashboard
        </button>
        <p className="error">{error}</p>
      </main>
    );
  }

  return (
    <main className="screen parent-goals">
      <button type="button" className="link-button" onClick={onBack}>
        ← Back to dashboard
      </button>
      <header className="screen-header">
        <h1>Goals</h1>
        <p className="tagline">
          Read-only view. Talk to your SLP if you&rsquo;d like to add or
          change a goal.
        </p>
      </header>

      {goals.length === 0 && (
        <p className="tagline">No goals yet.</p>
      )}

      {goals.map((g) => (
        <article key={g.goal_id} className="card stack-md">
          <div className="caseload-row-main">
            <span className="caseload-row-name">{g.area}</span>
            <span className="caseload-chip">{g.status}</span>
          </div>
          <p>{g.goal_text}</p>
        </article>
      ))}
    </main>
  );
}
